import { useEffect, useState } from "react";
import { api, formatApiError, fmtMoney, logoSrc } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { Settings, Users, Sliders, Flag, Building2, Plus, Trash2, Upload, ShoppingBag, Trophy, FileDown, Landmark, Undo2 } from "lucide-react";
import LogoUpload from "../../components/LogoUpload";

export default function LeagueAdmin() {
  const { league, teams, reload, isAdmin } = useLeague();
  const [tab, setTab] = useState("league");

  if (!isAdmin) {
    return <div className="border border-rose-500/40 bg-rose-500/10 rounded-3xl p-6 text-rose-300 shadow-xl shadow-rose-500/10">🔒 Solo l&apos;amministratore della lega può accedere a questa sezione.</div>;
  }

  const toggleArchive = async () => {
    const msg = league.archived
      ? "Ripristinare la lega tra quelle 'in corso'?"
      : "Archiviare la lega? Verrà spostata nell'archivio della Dashboard (potrai ripristinarla in ogni momento).";
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/leagues/${league.league_id}/archive`);
      toast.success(r.data.archived ? "📦 Lega archiviata" : "✅ Lega ripristinata");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold">👑 Admin</div>
          <h2 className="text-3xl font-black tracking-tight">Pannello amministratore</h2>
        </div>
        <button onClick={toggleArchive} data-testid="league-archive-toggle"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl border font-bold uppercase text-xs shadow-md shadow-black/40 transition-all ${
            league.archived
              ? "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
              : "border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
          }`}>
          {league.archived ? "♻️ Ripristina lega" : "📦 Archivia lega"}
        </button>
      </div>

      <div className="flex gap-1 border-b border-slate-800 overflow-x-auto">
        <Tab active={tab==="league"} onClick={()=>setTab("league")} icon={<Building2 size={14}/>} label="🏠 Lega" testid="tab-league-settings"/>
        <Tab active={tab==="teams"} onClick={()=>setTab("teams")} icon={<Users size={14}/>} label="⚽ Squadre" testid="tab-teams"/>
        <Tab active={tab==="params"} onClick={()=>setTab("params")} icon={<Sliders size={14}/>} label="🎛️ Parametri" testid="tab-params"/>
        <Tab active={tab==="renewal"} onClick={()=>setTab("renewal")} icon={<Settings size={14}/>} label="🔒 Livelli rinnovo" testid="tab-renewal"/>
        <Tab active={tab==="competitions"} onClick={()=>setTab("competitions")} icon={<Trophy size={14}/>} label="🏆 Competizioni" testid="tab-competitions"/>
        <Tab active={tab==="merch"} onClick={()=>setTab("merch")} icon={<ShoppingBag size={14}/>} label="🛍️ Merch" testid="tab-merch"/>
        <Tab active={tab==="finance"} onClick={()=>setTab("finance")} icon={<Landmark size={14}/>} label="💰 Finanze" testid="tab-finance"/>
        <Tab active={tab==="undo-log"} onClick={()=>setTab("undo-log")} icon={<Undo2 size={14}/>} label="⏪ Storico annullamenti" testid="tab-undo-log"/>
        <Tab active={tab==="close"} onClick={()=>setTab("close")} icon={<Flag size={14}/>} label="🏁 Chiudi stagione" testid="tab-close"/>
      </div>

      {tab === "league" && <LeagueSettingsTab league={league} reload={reload}/>}
      {tab === "teams" && <TeamsTab league={league} teams={teams} reload={reload}/>}
      {tab === "params" && <ParamsTab league={league} reload={reload}/>}
      {tab === "renewal" && <RenewalLevelsTab league={league} reload={reload}/>}
      {tab === "competitions" && <CompetitionsTab league={league} reload={reload}/>}
      {tab === "merch" && <MerchTab league={league} teams={teams} reload={reload}/>}
      {tab === "finance" && <FinanceConfigTab league={league} reload={reload}/>}
      {tab === "undo-log" && <UndoLogTab league={league}/>}
      {tab === "close" && <CloseSeasonTab league={league} teams={teams} reload={reload}/>}
    </div>
  );
}

function Tab({ active, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wider border-b-2 -mb-px whitespace-nowrap ${
        active ? "border-amber-400 text-amber-300" : "border-transparent text-slate-400 hover:text-white"
      }`}>{icon} {label}</button>
  );
}

/* ==================== TEAMS TAB ==================== */
function TeamsTab({ league, teams, reload }) {
  const [name, setName] = useState("");
  const [logoId, setLogoId] = useState(null);
  const [managerId, setManagerId] = useState("");

  const createTeam = async () => {
    if (!name) { toast.error("Nome squadra richiesto"); return; }
    try {
      await api.post(`/leagues/${league.league_id}/teams`, { name, logo_url: logoId || null, manager_id: managerId || null });
      toast.success(`⚽ Squadra "${name}" creata!`);
      setName(""); setLogoId(null); setManagerId("");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateTeamLogo = async (teamId, newLogo) => {
    try {
      await api.patch(`/leagues/${league.league_id}/teams/${teamId}`, { logo_url: newLogo || "" });
      toast.success("🖼️ Logo aggiornato");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upgradeStadium = async (teamId) => {
    try { await api.post(`/leagues/${league.league_id}/teams/${teamId}/stadium/upgrade`); toast.success("🏛️ Stadio potenziato!"); await reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-slate-800 rounded-lg bg-slate-900 p-6">
        <h3 className="font-bold text-lg mb-3">➕ Nuova squadra</h3>
        <p className="text-xs text-slate-400 mb-4">💡 Lascia <strong className="text-emerald-300">manager_id vuoto</strong> per creare una <strong>squadra libera</strong> — un manager potrà claimarla dopo aver inserito il codice invito.</p>
        <div className="grid md:grid-cols-2 gap-4 items-start">
          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Nome squadra</label>
              <input placeholder="Milan…" value={name} onChange={e=>setName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5"
                data-testid="admin-team-name"/>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">manager_id (vuoto = 🎟️ squadra libera)</label>
              <input placeholder="lascia vuoto per libera" value={managerId} onChange={e=>setManagerId(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 font-mono text-xs"/>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">🖼️ Logo (opz)</label>
            <LogoUpload value={logoId} onChange={setLogoId} scope="team" size="lg" testid="admin-team-logo"/>
          </div>
        </div>
        <button onClick={createTeam} data-testid="admin-team-create"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase">
          <Plus size={14}/> Crea
        </button>
      </div>

      <div className="border border-slate-800 rounded-lg bg-slate-900 overflow-hidden">
        <div className="p-4 border-b border-slate-800 font-bold uppercase text-sm">⚽ Squadre ({teams.length})</div>
        {teams.length === 0 ? <div className="p-8 text-center text-slate-500">Nessuna squadra ancora.</div> :
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-slate-950/60">
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-3 py-3">Logo</th>
              <th className="px-3 py-3">Nome</th>
              <th className="px-3 py-3">Manager</th>
              <th className="px-3 py-3 text-right">💰 Budget</th>
              <th className="px-3 py-3 text-right">🏛️ Stadio</th>
              <th className="px-3 py-3 text-right">⭐ Ranking</th>
              <th className="px-3 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {teams.map(t => (
              <tr key={t.team_id} className="border-t border-slate-800" data-testid={`admin-team-${t.team_id}`}>
                <td className="px-3 py-2.5">
                  <LogoUpload value={t.logo_url} onChange={(v)=>updateTeamLogo(t.team_id, v)} scope="team" size="sm" testid={`team-logo-${t.team_id}`}/>
                </td>
                <td className="px-3 py-2.5 font-bold">{t.name}</td>
                <td className="px-3 py-2.5 text-xs text-slate-400">{t.manager_name} <span className="text-slate-600">({t.manager_email})</span></td>
                <td className="px-3 py-2.5 text-right font-mono text-emerald-400">{fmtMoney(t.budget)}</td>
                <td className="px-3 py-2.5 text-right">Lv {t.stadium_level || 0}</td>
                <td className="px-3 py-2.5 text-right font-mono">{fmtMoney(t.ranking)}</td>
                <td className="px-3 py-2.5">
                  <button onClick={()=>upgradeStadium(t.team_id)}
                    data-testid={`upgrade-stadium-${t.team_id}`}
                    className="text-[10px] px-2 py-1 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/20">
                    🏛️ + Livello
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>}
      </div>
    </div>
  );
}

/* ==================== MERCH TAB ==================== */
function MerchTab({ league, teams, reload }) {
  const [tiers, setTiers] = useState(league.settings?.merch_tiers || []);
  const [posBonus, setPosBonus] = useState(league.settings?.merch_position_bonus || []);
  const [saving, setSaving] = useState(false);

  const updateTier = (i, k, v) => { const a = [...tiers]; a[i] = {...a[i], [k]: parseFloat(v) || 0}; setTiers(a); };
  const addTier = () => setTiers([...tiers, { min_roster_value: 0, income: 0 }]);
  const removeTier = (i) => setTiers(tiers.filter((_,x)=>x!==i));

  const updatePos = (i, v) => { const a = [...posBonus]; a[i] = parseFloat(v) || 0; setPosBonus(a); };
  const addPos = () => setPosBonus([...posBonus, 0]);
  const removePos = (i) => setPosBonus(posBonus.filter((_,x)=>x!==i));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leagues/${league.league_id}`, { settings: { merch_tiers: tiers, merch_position_bonus: posBonus }});
      toast.success("✅ Merchandising salvato");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Preview calc: usa valore rosa REALE di ogni squadra (già in team snapshot? — no, va calcolato)
  // Semplificazione: mostro la simulazione per una posizione data + un valore rosa simulato oppure per ogni squadra
  const [simRosterValue, setSimRosterValue] = useState(120);
  const [simPos, setSimPos] = useState(1);

  const sortedTiers = [...tiers].sort((a,b) => (b.min_roster_value || 0) - (a.min_roster_value || 0));
  const matchedTier = sortedTiers.find(t => simRosterValue >= (t.min_roster_value || 0));
  const baseIncome = matchedTier?.income || 0;
  const posBonusVal = posBonus[Math.max(0, simPos - 1)] || 0;
  const totalIncome = baseIncome * (1 + posBonusVal);

  return (
    <div className="space-y-4">
      <div className="border border-slate-800 rounded-lg bg-slate-900 p-6 space-y-4">
        <div className="border-l-2 border-amber-400 pl-3">
          <h3 className="font-bold text-lg">🛍️ Merchandising</h3>
          <p className="text-sm text-slate-400 mt-1">Formula: <code className="text-emerald-300">income_finale = tier(valore_rosa) × (1 + bonus_posizione)</code></p>
        </div>

        {/* Tiers */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase font-bold text-slate-400">📊 Fasce (per valore rosa)</div>
            <button onClick={addTier} className="text-[10px] px-3 py-1.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 font-bold">+ Fascia</button>
          </div>
          <div className="space-y-2">
            {tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                <div>
                  <label className="text-[9px] text-slate-500 uppercase block mb-0.5">Valore rosa ≥</label>
                  <input type="number" value={t.min_roster_value || 0} onChange={e=>updateTier(i, "min_roster_value", e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"
                    data-testid={`merch-tier-min-${i}`}/>
                </div>
                <div>
                  <label className="text-[9px] text-slate-500 uppercase block mb-0.5">Income base (cr)</label>
                  <input type="number" step="0.1" value={t.income || 0} onChange={e=>updateTier(i, "income", e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"
                    data-testid={`merch-tier-income-${i}`}/>
                </div>
                <button onClick={()=>removeTier(i)} className="text-rose-400 hover:text-rose-300 mt-4"><Trash2 size={14}/></button>
              </div>
            ))}
            {tiers.length === 0 && <div className="text-slate-500 text-sm py-2">Nessuna fascia. Aggiungi la prima.</div>}
          </div>
        </div>

        {/* Position bonus */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase font-bold text-slate-400">🏆 Bonus % per posizione (1° → N°)</div>
            <button onClick={addPos} className="text-[10px] px-3 py-1.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 font-bold">+ Posizione</button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="merch-pos-bonus">
            {posBonus.map((v, i) => (
              <div key={i} className="rounded border border-slate-800 p-2 text-center">
                <div className="text-[9px] text-slate-500 uppercase font-bold">#{i+1}</div>
                <input type="number" step="0.01" value={v} onChange={e=>updatePos(i, e.target.value)}
                  className="w-16 bg-slate-950 border border-slate-800 rounded px-1 py-1 text-center text-sm font-mono"/>
                <button onClick={()=>removePos(i)} className="text-slate-500 hover:text-rose-400 text-xs block mx-auto mt-1">×</button>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">Esempio: 0.30 = +30%</div>
        </div>

        <button onClick={save} disabled={saving}
          data-testid="merch-save"
          className="w-full px-4 py-3 rounded-md bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold uppercase disabled:opacity-50">
          {saving ? "Salvataggio…" : "💾 Salva merchandising"}
        </button>
      </div>

      {/* Preview simulator */}
      <div className="border border-emerald-500/30 rounded-lg bg-emerald-500/5 p-6 space-y-3" data-testid="merch-preview">
        <div className="flex items-center gap-2">
          <ShoppingBag size={16} className="text-emerald-400"/>
          <h3 className="font-bold text-lg">🔮 Anteprima calcolo</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">💎 Simula valore rosa</label>
            <input type="range" min="0" max="300" value={simRosterValue} onChange={e=>setSimRosterValue(Number(e.target.value))}
              className="w-full" data-testid="merch-sim-roster"/>
            <div className="font-mono text-emerald-300 text-xl">{simRosterValue} cr</div>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">🏆 Simula posizione</label>
            <input type="range" min="1" max={Math.max(posBonus.length, 10)} value={simPos} onChange={e=>setSimPos(Number(e.target.value))}
              className="w-full" data-testid="merch-sim-pos"/>
            <div className="font-mono text-amber-300 text-xl">#{simPos}°</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center pt-2">
          <div className="rounded bg-slate-900/60 p-3 border border-slate-800">
            <div className="text-[10px] uppercase text-slate-500 font-bold">Base tier</div>
            <div className="font-mono text-lg font-black text-white">{baseIncome.toFixed(2)}</div>
            <div className="text-[10px] text-slate-500">min ≥ {matchedTier?.min_roster_value ?? "—"}</div>
          </div>
          <div className="rounded bg-slate-900/60 p-3 border border-slate-800">
            <div className="text-[10px] uppercase text-slate-500 font-bold">+ Bonus pos</div>
            <div className="font-mono text-lg font-black text-amber-300">{(posBonusVal * 100).toFixed(0)}%</div>
            <div className="text-[10px] text-slate-500">×(1+{posBonusVal.toFixed(2)})</div>
          </div>
          <div className="rounded bg-emerald-500/20 p-3 border border-emerald-500/40">
            <div className="text-[10px] uppercase text-emerald-300 font-bold">= Income finale</div>
            <div className="font-mono text-2xl font-black text-emerald-300">{totalIncome.toFixed(2)}</div>
            <div className="text-[10px] text-emerald-400/70">cr/stagione</div>
          </div>
        </div>

        {/* Per-team preview if teams exist */}
        {teams.length > 0 && (
          <div className="mt-3 pt-3 border-t border-emerald-500/20">
            <div className="text-[10px] uppercase text-slate-400 font-bold mb-2">💡 Suggerimento: le squadre attualmente hanno budget diverso. Il valore rosa reale sarà applicato alla chiusura stagione.</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== PARAMS TAB (generic settings) ==================== */
function ParamsTab({ league, reload }) {
  const [s, setS] = useState(league.settings);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leagues/${league.league_id}`, { settings: s });
      toast.success("✅ Parametri salvati");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setS({ ...s, [k]: v });
  const setArr = (k, i, v) => { const a = [...(s[k]||[])]; a[i] = v; setS({...s, [k]: a}); };

  return (
    <div className="space-y-4">
      <div className="border border-slate-800 rounded-lg bg-slate-900 p-6 space-y-4">
        <h3 className="font-bold text-lg">🎛️ Parametri lega</h3>

        <div className="grid md:grid-cols-3 gap-3">
          <NumField label="Budget iniziale (cr)" v={s.initial_budget} onChange={v=>set("initial_budget", v)} testid="pset-budget"/>
          <NumField label="Numero squadre" v={s.num_teams} onChange={v=>set("num_teams", v)} testid="pset-teams"/>
          <NumField label="Stip. default %" step="0.01" v={s.default_salary_pct} onChange={v=>set("default_salary_pct", v)} testid="pset-salpct"/>
          <NumField label="Diritti TV / stagione" v={s.tv_rights} onChange={v=>set("tv_rights", v)} testid="pset-tv"/>
        </div>

        <ArrEditor label="🏆 Premi per posizione (1° → 10°)" values={s.prize_table} onChange={v=>set("prize_table", v)} testid="pset-prizes"/>
        <ArrEditor label="⭐ Ranking per posizione" values={s.ranking_table} onChange={v=>set("ranking_table", v)} testid="pset-rank"/>
        <ArrEditor label="🛍️ Bonus merch % per posizione" values={s.merch_position_bonus} step="0.01" onChange={v=>set("merch_position_bonus", v)} testid="pset-merch"/>

        <div>
          <div className="text-xs uppercase font-bold text-slate-400 mb-2">🚪 Recupero crediti svincolo</div>
          <div className="grid grid-cols-3 gap-3">
            <NumField label="Svincolo libero %" step="0.01" v={s.release_recovery?.svincolo_libero} onChange={v=>set("release_recovery", {...s.release_recovery, svincolo_libero: v})} testid="pset-rec-libero"/>
            <NumField label="Ceduto esterno %" step="0.01" v={s.release_recovery?.ceduto_esterno} onChange={v=>set("release_recovery", {...s.release_recovery, ceduto_esterno: v})} testid="pset-rec-esterno"/>
            <NumField label="Ritirato %" step="0.01" v={s.release_recovery?.ritirato} onChange={v=>set("release_recovery", {...s.release_recovery, ritirato: v})} testid="pset-rec-ritirato"/>
          </div>
        </div>

        <button onClick={save} disabled={saving}
          data-testid="pset-save"
          className="w-full px-4 py-3 rounded-md bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold uppercase disabled:opacity-50">
          {saving ? "Salvataggio…" : "💾 Salva parametri"}
        </button>
      </div>
    </div>
  );
}

/* ==================== RENEWAL LEVELS TAB ==================== */
function RenewalLevelsTab({ league, reload }) {
  const [levels, setLevels] = useState(league.settings.renewal_levels || []);
  const [saving, setSaving] = useState(false);

  const update = (i, k, v) => { const a = [...levels]; a[i] = {...a[i], [k]: v}; setLevels(a); };
  const add = () => setLevels([...levels, { level: levels.length + 1, salary_pct: 0.10, auction_bonus: 0, blindato: false, label: `Livello ${levels.length + 1}` }]);
  const remove = (i) => setLevels(levels.filter((_,x)=>x!==i));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leagues/${league.league_id}`, { settings: { renewal_levels: levels }});
      toast.success("✅ Livelli salvati");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-800 rounded-lg bg-slate-900 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">🔒 Livelli di rinnovo</h3>
        <button onClick={add} data-testid="add-level"
          className="text-xs px-3 py-1.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 font-bold">
          + Aggiungi livello
        </button>
      </div>
      <div className="space-y-2">
        {levels.map((l, i) => (
          <div key={i} className={`grid md:grid-cols-6 gap-2 items-center p-3 rounded border ${l.blindato ? "border-rose-500/40 bg-rose-500/5" : "border-slate-800"}`}>
            <input value={l.label || ""} onChange={e=>update(i, "label", e.target.value)} placeholder="Etichetta"
              className="bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm"/>
            <input type="number" value={l.level} onChange={e=>update(i, "level", parseInt(e.target.value))} placeholder="Numero"
              className="bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"/>
            <div>
              <label className="text-[9px] text-slate-500 uppercase">Stip. %</label>
              <input type="number" step="0.01" value={l.salary_pct} onChange={e=>update(i, "salary_pct", parseFloat(e.target.value))}
                className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"/>
            </div>
            <div>
              <label className="text-[9px] text-slate-500 uppercase">Bonus asta</label>
              <input type="number" value={l.auction_bonus} onChange={e=>update(i, "auction_bonus", parseInt(e.target.value))}
                className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"/>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!l.blindato} onChange={e=>update(i, "blindato", e.target.checked)}/>
              🔒 Blindato
            </label>
            <button onClick={()=>remove(i)} className="text-rose-400 hover:text-rose-300 text-sm inline-flex items-center gap-1"><Trash2 size={12}/> Elimina</button>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving}
        data-testid="save-levels"
        className="w-full px-4 py-3 rounded-md bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold uppercase disabled:opacity-50">
        💾 Salva livelli
      </button>
    </div>
  );
}

/* ==================== CLOSE SEASON TAB ==================== */
function CloseSeasonTab({ league, teams, reload }) {
  const [positions, setPositions] = useState(() => teams.map((t, i) => ({ team_id: t.team_id, position: i + 1 })));
  const [compResults, setCompResults] = useState({}); // {competition_id: [team_id, team_id, ...]}
  const [notes, setNotes] = useState("");
  const [running, setRunning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const competitions = (league.settings?.competitions || []);
  // Escludi lo "scudetto" perché è derivato dalla classifica sopra
  const extraComps = competitions.filter(c => c.id !== "scudetto");

  useEffect(() => { setPositions(teams.map((t, i) => ({ team_id: t.team_id, position: i + 1 }))); }, [teams]);

  const setPos = (team_id, position) => {
    setPositions(prev => prev.map(p => p.team_id === team_id ? { ...p, position: parseInt(position) || 0 } : p));
  };

  const setCompPos = (compId, idx, teamId) => {
    setCompResults(prev => {
      const cur = [...(prev[compId] || [])];
      // ensure length
      while (cur.length <= idx) cur.push("");
      cur[idx] = teamId;
      return { ...prev, [compId]: cur };
    });
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/leagues/${league.league_id}/imports/classifica`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const res = r.data;
      setImportResult(res);
      const matchedMap = Object.fromEntries((res.matched || []).map(m => [m.team_id, m.position]));
      setPositions(prev => prev.map(p =>
        matchedMap[p.team_id] != null ? { ...p, position: matchedMap[p.team_id] } : p
      ));
      const okCount = (res.matched || []).length;
      const koCount = (res.unmatched || []).length;
      if (okCount > 0) toast.success(`✅ ${okCount} squadre riconosciute${koCount ? `, ${koCount} non trovate` : ""}`);
      else toast.error("Nessuna squadra riconosciuta");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!window.confirm(`Chiudere la stagione ${league.season}? Questa azione è irreversibile e archivierà tutti i dati.`)) return;
    setRunning(true);
    try {
      // Assicura che "scudetto" sia allineato con la classifica se presente
      const results = [];
      const scudetto = competitions.find(c => c.id === "scudetto");
      if (scudetto) {
        const scudettoPos = [...positions].sort((a, b) => a.position - b.position).map(p => p.team_id);
        results.push({ competition_id: "scudetto", positions: scudettoPos });
      }
      for (const c of extraComps) {
        const arr = (compResults[c.id] || []).filter(Boolean);
        if (arr.length > 0) results.push({ competition_id: c.id, positions: arr });
      }
      const r = await api.post(`/leagues/${league.league_id}/close-season`, {
        positions,
        competition_results: results,
        notes,
      });
      toast.success(`🎬 Stagione ${league.season} chiusa! Benvenuto nella nuova stagione.`);
      console.log("season closed:", r.data);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 space-y-4 shadow-xl shadow-black/40">
      <div className="border-l-4 border-amber-400 pl-3">
        <h3 className="font-bold text-lg">🏁 Chiudi stagione {league.season}</h3>
        <p className="text-sm text-slate-400 mt-1">Inserisci la classifica finale del Campionato e i podi delle altre competizioni. L&apos;app calcolerà premi, TV, merch, ranking, trofei e archivierà tutto.</p>
      </div>

      {/* Import Excel Classifica */}
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-emerald-300">📥 Importa classifica da Excel/CSV</div>
            <div className="text-xs text-slate-400 mt-0.5">Colonne: <code className="text-amber-300">Squadra</code>, <code className="text-amber-300">Posizione</code> (opz).</div>
          </div>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase text-xs cursor-pointer" data-testid="import-classifica-btn">
            <Upload size={14}/>
            {importing ? "Caricamento…" : "Scegli file"}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={onImportFile} className="hidden" disabled={importing} data-testid="import-classifica-file"/>
          </label>
        </div>
        {importResult && (
          <div className="mt-3 text-xs space-y-1" data-testid="import-classifica-result">
            <div className="text-emerald-300">✅ {(importResult.matched || []).length} riconosciute su {importResult.total_rows} righe</div>
            {(importResult.unmatched || []).length > 0 && (
              <details className="text-slate-400">
                <summary className="cursor-pointer text-amber-300">⚠️ {importResult.unmatched.length} non trovate</summary>
                <ul className="mt-1 ml-4 font-mono">
                  {importResult.unmatched.map((u, i) => <li key={i}>• {u.raw_name} (pos {u.position})</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase font-bold text-slate-400">🏆 Classifica Campionato ({competitions.find(c=>c.id==='scudetto')?.emoji || '🏆'} Scudetto)</div>
        {teams.map(t => {
          const pos = positions.find(p => p.team_id === t.team_id)?.position || "";
          return (
            <div key={t.team_id} className="flex items-center gap-3 p-2 border border-slate-800 rounded-2xl" data-testid={`pos-${t.team_id}`}>
              <input type="number" min="1" max={teams.length} value={pos} onChange={e=>setPos(t.team_id, e.target.value)}
                className="w-16 bg-slate-950 border border-slate-800 rounded-xl px-2 py-1.5 text-center font-mono font-bold"/>
              <div className="flex-1 font-bold">⚽ {t.name}</div>
            </div>
          );
        })}
      </div>

      {/* Altre competizioni */}
      {extraComps.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs uppercase font-bold text-slate-400">🥇 Altre competizioni (podio)</div>
          {extraComps.map(c => {
            const maxPos = Math.min(c.prize_positions?.length || 3, 5);
            return (
              <div key={c.id} className="border border-slate-800 rounded-2xl p-3 space-y-2" data-testid={`close-comp-${c.id}`}>
                <div className="flex items-center gap-2 font-bold">
                  <span className="text-lg">{c.emoji}</span>
                  {c.name}
                  <span className="text-[10px] text-slate-500">→ Titolo: {c.title_awarded}</span>
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {Array.from({length: maxPos}).map((_, idx) => {
                    const val = (compResults[c.id] || [])[idx] || "";
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-slate-500 w-6">{idx+1}°</span>
                        <select value={val} onChange={e=>setCompPos(c.id, idx, e.target.value)}
                          data-testid={`close-comp-${c.id}-pos-${idx+1}`}
                          className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-2 py-1.5 text-sm">
                          <option value="">— seleziona —</option>
                          {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
                        </select>
                        <span className="text-[10px] font-mono text-amber-300 w-12 text-right">
                          {c.prize_positions?.[idx] || 0} cr
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div>
        <label className="text-xs uppercase font-bold text-slate-400 block mb-2">📝 Note (opz)</label>
        <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
          className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2 text-sm"
          data-testid="close-notes"/>
      </div>

      <button onClick={submit} disabled={running}
        data-testid="close-submit"
        className="w-full px-4 py-3 rounded-2xl bg-gradient-to-br from-rose-500 to-rose-700 hover:from-rose-400 hover:to-rose-600 text-white font-bold uppercase disabled:opacity-50 shadow-lg shadow-rose-500/30">
        {running ? "Chiusura…" : `🎬 Chiudi Stagione ${league.season}`}
      </button>
    </div>
  );
}

/* ==================== small components ==================== */
function NumField({ label, v, onChange, step, testid }) {
  return (
    <div>
      <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">{label}</label>
      <input type="number" step={step || 1} value={v ?? 0} onChange={e=>onChange(step ? parseFloat(e.target.value) : parseInt(e.target.value))}
        className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm font-mono"
        data-testid={testid}/>
    </div>
  );
}
function ArrEditor({ label, values, step, onChange, testid }) {
  const values2 = values || [];
  const update = (i, v) => { const a = [...values2]; a[i] = step ? parseFloat(v) : parseInt(v); onChange(a); };
  const add = () => onChange([...values2, 0]);
  const remove = (i) => onChange(values2.filter((_,x)=>x!==i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase font-bold text-slate-400">{label}</div>
        <button onClick={add} className="text-[10px] px-2 py-1 rounded border border-slate-700 hover:border-emerald-500 text-slate-300">+ Aggiungi</button>
      </div>
      <div className="flex flex-wrap gap-1" data-testid={testid}>
        {values2.map((v, i) => (
          <div key={i} className="flex items-center gap-1">
            <input type="number" step={step || 1} value={v} onChange={e=>update(i, e.target.value)}
              className="w-20 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm font-mono"/>
            <button onClick={()=>remove(i)} className="text-slate-500 hover:text-rose-400 text-xs">×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==================== COMPETITIONS TAB ==================== */
function CompetitionsTab({ league, reload }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const initial = league.settings?.competitions || [];
  const [items, setItems] = useState(initial);
  const [dirty, setDirty] = useState(false);

  const update = (i, field, val) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: val };
    setItems(next); setDirty(true);
  };
  const updatePrize = (i, pos, val) => {
    const next = [...items];
    const prizes = [...(next[i].prize_positions || [])];
    prizes[pos] = Number(val || 0);
    next[i] = { ...next[i], prize_positions: prizes };
    setItems(next); setDirty(true);
  };
  const addPrize = (i) => {
    const next = [...items];
    next[i] = { ...next[i], prize_positions: [...(next[i].prize_positions || []), 0] };
    setItems(next); setDirty(true);
  };
  const removePrize = (i) => {
    const next = [...items];
    const prizes = [...(next[i].prize_positions || [])];
    prizes.pop();
    next[i] = { ...next[i], prize_positions: prizes };
    setItems(next); setDirty(true);
  };
  const addComp = () => {
    const nid = "comp_" + Math.random().toString(36).slice(2, 7);
    setItems([...items, { id: nid, name: "Nuova competizione", emoji: "🏆", prize_positions: [10, 5], title_awarded: "Titolo", description: "" }]);
    setDirty(true);
  };
  const removeComp = (i) => {
    if (!window.confirm("Rimuovere questa competizione? Lo storico non verrà cancellato.")) return;
    setItems(items.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  const save = async () => {
    try {
      await api.put(`/leagues/${league.league_id}/settings/competitions`, { competitions: items });
      toast.success("🏆 Competizioni aggiornate");
      setDirty(false);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = (cid, name) => {
    window.open(`${backendUrl}/api/leagues/${league.league_id}/competitions/${cid}/bilancio.pdf`, "_blank");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-black uppercase tracking-wider text-lg">🏆 Competizioni della lega</h3>
          <p className="text-slate-400 text-sm">Configura nome, premi per posizione, titolo assegnato. A fine stagione l&apos;admin inserisce la classifica per ognuna e genera i PDF.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addComp} data-testid="add-competition-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-sm font-bold">
            <Plus size={14}/> Aggiungi
          </button>
          {dirty && (
            <button onClick={save} data-testid="save-competitions-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-slate-950 font-black uppercase text-xs shadow-lg shadow-amber-500/40">
              💾 Salva modifiche
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((c, i) => (
          <div key={c.id + i} data-testid={`comp-card-${c.id}`}
            className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-xl shadow-black/40 space-y-3">
            <div className="flex items-center gap-2">
              <input value={c.emoji} onChange={e=>update(i,"emoji",e.target.value)} maxLength={4}
                className="w-14 bg-slate-950 border border-slate-800 rounded-2xl px-2 py-1.5 text-center text-xl"/>
              <input value={c.name} onChange={e=>update(i,"name",e.target.value)}
                className="flex-1 bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2 text-sm font-bold" placeholder="Nome"/>
              <button onClick={()=>removeComp(i)} className="text-rose-400 hover:bg-rose-500/10 px-2 py-1 rounded-xl">
                <Trash2 size={16}/>
              </button>
            </div>
            <input value={c.title_awarded || ""} onChange={e=>update(i,"title_awarded",e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2 text-xs" placeholder="Nome del titolo assegnato (es. Scudetto)"/>
            <input value={c.description || ""} onChange={e=>update(i,"description",e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2 text-xs text-slate-400" placeholder="Descrizione breve"/>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex justify-between">
                <span>💰 Premi per posizione (in crediti)</span>
                <span className="flex gap-1">
                  <button onClick={()=>removePrize(i)} className="text-slate-500 hover:text-rose-400">−</button>
                  <button onClick={()=>addPrize(i)} className="text-slate-500 hover:text-emerald-400">+</button>
                </span>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-1">
                {(c.prize_positions || []).map((v, pos) => (
                  <label key={pos} className="text-[10px] text-slate-500">
                    {pos+1}°
                    <input type="number" value={v}
                      onChange={e=>updatePrize(i, pos, e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-2 py-1 text-sm text-white font-mono"/>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={()=>downloadPdf(c.id, c.name)} data-testid={`pdf-comp-${c.id}`}
                className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 px-3 py-1.5 rounded-xl">
                <FileDown size={12}/> Albo d&apos;oro PDF
              </button>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="border border-dashed border-slate-800 rounded-3xl p-8 text-center text-slate-500">
          Nessuna competizione. Cliccare &quot;Aggiungi&quot; per crearne una.
        </div>
      )}
    </div>
  );
}


/* ==================== LEAGUE SETTINGS TAB ==================== */
const PRESET_COLORS = [
  { name: "Ambra", value: "#f59e0b" },   // default
  { name: "Smeraldo", value: "#10b981" },
  { name: "Rosa", value: "#e11d48" },
  { name: "Blu", value: "#3b82f6" },
  { name: "Viola", value: "#a855f7" },
  { name: "Ciano", value: "#06b6d4" },
];
function LeagueSettingsTab({ league, reload }) {
  const [name, setName] = useState(league.name || "");
  const [logo, setLogo] = useState(league.logo_url || null);
  const [themeColor, setThemeColor] = useState(league.theme_color || "#f59e0b");
  const [deadline, setDeadline] = useState(league.settings?.auction_deadline || "");
  const [saving, setSaving] = useState(false);
  const [savingDeadline, setSavingDeadline] = useState(false);
  const [skipAstaFurti, setSkipAstaFurti] = useState(Boolean(league.settings?.skip_asta_furti));
  const [savingSkip, setSavingSkip] = useState(false);
  const initialTheme = league.theme_color || "#f59e0b";
  const dirty = name !== league.name || logo !== league.logo_url || themeColor !== initialTheme;
  const themeInvalid = Boolean(themeColor) && !/^#[0-9A-Fa-f]{6}$/.test(themeColor);

  const toggleSkipAstaFurti = async () => {
    const next = !skipAstaFurti;
    setSavingSkip(true);
    try {
      await api.post(`/leagues/${league.league_id}/phase/skip-asta-furti`, { enabled: next });
      setSkipAstaFurti(next);
      toast.success(next ? "🎯 Asta Furti disattivata: la fase 10 verrà saltata" : "🎯 Asta Furti riattivata");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSkip(false); }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Il nome della lega non può essere vuoto"); return; }
    // Validate hex
    if (themeColor && !/^#[0-9A-Fa-f]{6}$/.test(themeColor)) {
      toast.error("Colore non valido. Usa formato #RRGGBB");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/leagues/${league.league_id}`, { name: name.trim(), logo_url: logo, theme_color: themeColor });
      toast.success("🏠 Lega aggiornata");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveDeadline = async () => {
    setSavingDeadline(true);
    try {
      // Convert HTML datetime-local to ISO with Z
      const iso = deadline ? new Date(deadline).toISOString() : null;
      await api.post(`/leagues/${league.league_id}/auction-deadline`, { auction_deadline: iso });
      toast.success(iso ? "⏰ Scadenza asta impostata" : "🗑️ Scadenza rimossa");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingDeadline(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(league.code);
    toast.success(`Codice ${league.code} copiato!`);
  };

  return (
    <div className="space-y-4">
      <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 shadow-xl shadow-black/40 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-black uppercase tracking-wider text-lg">🏠 Dati generali</h3>
            <p className="text-slate-400 text-sm">Modifica nome, logo e visualizza codice invito.</p>
          </div>
          {dirty && (
            <button onClick={save} disabled={saving || themeInvalid} data-testid="save-league-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-slate-950 font-black uppercase text-xs shadow-lg shadow-amber-500/40 disabled:opacity-50">
              {saving ? "Salvo…" : "💾 Salva"}
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Nome lega</label>
              <input value={name} onChange={e=>setName(e.target.value)} data-testid="league-name-input"
                className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2.5 text-sm font-bold"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Codice invito</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2.5 text-sm font-mono font-black text-amber-300">{league.code}</code>
                <button onClick={copyCode} className="p-2 rounded-2xl border border-slate-700 hover:border-emerald-500 text-slate-400 hover:text-emerald-300" title="Copia codice" data-testid="copy-code-btn">
                  <Upload size={14} className="rotate-180"/>
                </button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Condividi questo codice per invitare manager alla lega.</div>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2">
              <MiniInfo label="Stagione" value={league.season} />
              <MiniInfo label="Ranking" value={league.settings?.ranking_table?.length ? `1° = ${league.settings.ranking_table[0]}` : "—"} />
              <MiniInfo label="Budget iniz." value={league.settings?.initial_budget || 0} />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Logo lega</label>
            <LogoUpload value={logo} onChange={setLogo} label="lega" testid="league-logo-upload"/>
          </div>
        </div>
      </div>

      {/* Personalizzazione tema */}
      <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 shadow-xl shadow-black/40 space-y-4" data-testid="theme-customization-section">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-black uppercase tracking-wider text-lg flex items-center gap-2">🎨 Colore tema</h3>
            <p className="text-slate-400 text-sm">Personalizza il colore d&apos;accento della tua lega. Viene applicato al nome nell&apos;header, ai tab attivi e ai badge.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-2">Preset</label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map(c => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setThemeColor(c.value)}
                  data-testid={`theme-preset-${c.value.replace('#','')}`}
                  title={c.name}
                  className={`w-11 h-11 rounded-xl border-2 transition-all ${themeColor.toLowerCase()===c.value.toLowerCase() ? 'border-white scale-110 shadow-lg' : 'border-slate-700 hover:scale-105'}`}
                  style={{ backgroundColor: c.value, boxShadow: themeColor.toLowerCase()===c.value.toLowerCase() ? `0 0 20px ${c.value}80` : undefined }}
                />
              ))}
            </div>
            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Custom (HEX #RRGGBB)</label>
              <div className="flex items-center gap-2">
                <input type="color" value={themeColor} onChange={e=>setThemeColor(e.target.value)}
                  data-testid="theme-color-picker"
                  className="h-10 w-16 rounded-md border border-slate-700 bg-slate-950 cursor-pointer"/>
                <input type="text" value={themeColor} onChange={e=>setThemeColor(e.target.value)}
                  data-testid="theme-color-hex"
                  placeholder="#f59e0b" maxLength={7}
                  className={`flex-1 bg-slate-950 border rounded-2xl px-3 py-2 text-sm font-mono uppercase ${themeInvalid ? 'border-red-500 text-red-400' : 'border-slate-800'}`}/>
              </div>
              {themeInvalid && <p className="text-red-400 text-xs mt-1" data-testid="theme-hex-error">Formato non valido. Usa #RRGGBB (es. #f59e0b)</p>}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-2">Anteprima</label>
            <div className="border border-slate-800 rounded-2xl bg-slate-950 p-4 space-y-3">
              <div className="flex items-center gap-2">
                {logoSrc(logo) && <img src={logoSrc(logo)} alt="" className="w-8 h-8 rounded object-cover border border-slate-700"/>}
                <div className="font-black uppercase text-lg leading-none" style={{ color: themeColor }}>{name || "Nome Lega"}</div>
                <span className="text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 rounded border" style={{ color: themeColor, backgroundColor: `${themeColor}22`, borderColor: `${themeColor}55` }}>👑 Admin</span>
              </div>
              <div className="flex gap-4 text-xs pt-2 border-t border-slate-800">
                <span className="font-bold uppercase tracking-wider pb-1 border-b-2" style={{ color: themeColor, borderColor: themeColor }}>Dashboard</span>
                <span className="text-slate-500 pb-1">Rosa</span>
                <span className="text-slate-500 pb-1">Mercato</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ResetSeasonSection league={league} reload={reload}/>

      {/* Auction deadline */}
      <div className="border border-amber-500/30 rounded-3xl bg-gradient-to-br from-amber-500/5 to-slate-950 p-6 shadow-xl shadow-black/40 space-y-3" data-testid="auction-deadline-section">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-black uppercase tracking-wider text-lg text-amber-300 flex items-center gap-2">⏰ Scadenza asta furto</h3>
            <p className="text-slate-400 text-sm">Imposta la data-ora dell&apos;asta. Un countdown apparirà in Dashboard e verrà mandata una notifica in-app a tutti i manager 24h prima.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input type="datetime-local" value={deadline ? deadline.slice(0,16) : ""} onChange={e=>setDeadline(e.target.value)}
            data-testid="auction-deadline-input"
            className="bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2 text-sm font-mono"/>
          <button onClick={saveDeadline} disabled={savingDeadline} data-testid="auction-deadline-save"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-slate-950 font-black uppercase text-xs shadow-lg shadow-amber-500/40 disabled:opacity-50">
            {savingDeadline ? "Salvo…" : "💾 Salva scadenza"}
          </button>
          {deadline && (
            <button onClick={()=>{setDeadline(""); saveDeadline();}} data-testid="auction-deadline-clear"
              className="text-[11px] text-slate-500 hover:text-rose-400 font-bold uppercase">
              🗑️ Rimuovi
            </button>
          )}
        </div>
        {league.settings?.auction_deadline && (
          <div className="text-xs text-amber-300">
            📅 Attualmente impostata: {new Date(league.settings.auction_deadline).toLocaleString("it-IT")}
          </div>
        )}
      </div>

      {/* Skip Asta Furti */}
      <div className="border border-orange-500/30 rounded-3xl bg-gradient-to-br from-orange-500/5 to-slate-950 p-6 shadow-xl shadow-black/40 space-y-3" data-testid="skip-asta-furti-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs tracking-[0.3em] text-orange-400 uppercase font-black">🎯 Fase 10 · Asta Furti</div>
            <div className="text-lg font-bold text-white mt-1">Salta l&apos;Asta Furti in questa lega</div>
            <div className="text-xs text-slate-400 mt-1 leading-relaxed max-w-xl">
              Se la tua lega non organizza l&apos;asta post-rinnovi, attiva questo flag: l&apos;avanzamento dalla Fase 9 (Approv. Rinnovi) porterà direttamente alla Fase 11 (Chiusura Economica), senza cliccare &quot;avanti&quot; su una fase inutile.
            </div>
          </div>
          <button onClick={toggleSkipAstaFurti} disabled={savingSkip}
            data-testid="btn-toggle-skip-asta-furti"
            className={`relative inline-flex items-center h-8 w-14 rounded-full border transition-all shrink-0 ${
              skipAstaFurti
                ? "bg-orange-500/40 border-orange-500/70 shadow-lg shadow-orange-500/20"
                : "bg-slate-800 border-slate-700"
            } ${savingSkip ? "opacity-50" : "hover:brightness-110"}`}
          >
            <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-lg transition-all ${skipAstaFurti ? "left-7" : "left-1"}`}/>
          </button>
        </div>
        {skipAstaFurti && (
          <div className="mt-2 rounded-xl bg-orange-500/10 border border-orange-500/40 px-3 py-2 flex items-center gap-2" data-testid="skip-asta-furti-badge">
            <span className="text-orange-300 text-sm font-bold">✓ Asta Furti disattivata</span>
            <span className="text-xs text-orange-300/70">— al prossimo advance dalla Fase 9 salterai direttamente alla Fase 11</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- Reset Nuova Stagione ---- */
function ResetSeasonSection({ league, reload }) {
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [keepBlindati, setKeepBlindati] = useState(true);
  const [resetBudgetsMode, setResetBudgetsMode] = useState("none"); // none | default | per-team
  const [budgetDefault, setBudgetDefault] = useState(league.settings?.initial_budget || 500);
  const [perTeam, setPerTeam] = useState({});
  const [running, setRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leagues/${league.league_id}/teams`);
        setTeams(r.data);
        setPerTeam(Object.fromEntries(r.data.map(t => [t.team_id, Math.round(t.budget || 0)])));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [league.league_id]);

  const run = async () => {
    if (!window.confirm(`⚠️ ATTENZIONE\n\nVerranno RIMOSSI tutti i giocatori delle rose${keepBlindati ? " (tranne i blindati)" : ""}.\n${resetBudgetsMode !== "none" ? "\nI budget saranno resettati." : ""}\n\nUsa questa funzione DOPO l'asta furto, per preparare la nuova stagione.\n\nProcedere?`)) return;
    setRunning(true);
    try {
      const body = { keep_blindati: keepBlindati };
      if (resetBudgetsMode === "default") {
        body.reset_budgets_to = Number(budgetDefault);
      } else if (resetBudgetsMode === "per-team") {
        body.per_team_budgets = Object.fromEntries(Object.entries(perTeam).map(([k, v]) => [k, Number(v || 0)]));
      }
      const r = await api.post(`/leagues/${league.league_id}/reset-rosters`, body);
      toast.success(`✅ ${r.data.players_removed} giocatori rimossi · ${r.data.teams_budget_reset} budget aggiornati`);
      setOpen(false);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  return (
    <div className="border border-rose-500/30 rounded-3xl bg-gradient-to-br from-rose-500/5 to-slate-950 p-6 shadow-xl shadow-black/40 space-y-3" data-testid="reset-season-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-black uppercase tracking-wider text-lg text-rose-300 flex items-center gap-2">🔄 Prepara nuova stagione</h3>
          <p className="text-slate-400 text-sm">Da usare <strong>dopo</strong> l&apos;asta furto: rimuove tutte le rose (opzionalmente conserva i blindati) e resetta i budget per accogliere le nuove rose finali.</p>
        </div>
        <button onClick={() => setOpen(v => !v)} data-testid="reset-season-toggle"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-rose-500/50 text-rose-300 hover:bg-rose-500/10 font-bold uppercase text-xs">
          {open ? "Chiudi" : "🔄 Configura Reset"}
        </button>
      </div>

      {open && (
        <div className="space-y-4 pt-2 border-t border-slate-800">
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-2xl border border-slate-800 bg-slate-950/40" data-testid="reset-keep-blindati">
            <input type="checkbox" checked={keepBlindati} onChange={e => setKeepBlindati(e.target.checked)} className="w-4 h-4"/>
            <div>
              <div className="font-bold text-sm">🔒 Mantieni i blindati</div>
              <div className="text-[11px] text-slate-500">I giocatori con contratto blindato restano in rosa (utile se il rinnovo garantisce presenza in squadra)</div>
            </div>
          </label>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">💰 Budget squadre</div>
            <div className="grid sm:grid-cols-3 gap-2">
              <button onClick={() => setResetBudgetsMode("none")}
                data-testid="reset-budget-mode-none"
                className={`p-3 rounded-2xl border text-xs font-bold uppercase transition-all ${resetBudgetsMode === "none" ? "border-slate-500 bg-slate-800 text-white" : "border-slate-800 text-slate-500 hover:border-slate-600"}`}>
                Non toccare
              </button>
              <button onClick={() => setResetBudgetsMode("default")}
                data-testid="reset-budget-mode-default"
                className={`p-3 rounded-2xl border text-xs font-bold uppercase transition-all ${resetBudgetsMode === "default" ? "border-amber-400 bg-amber-500/20 text-amber-300" : "border-slate-800 text-slate-500 hover:border-slate-600"}`}>
                Valore unico
              </button>
              <button onClick={() => setResetBudgetsMode("per-team")}
                data-testid="reset-budget-mode-per-team"
                className={`p-3 rounded-2xl border text-xs font-bold uppercase transition-all ${resetBudgetsMode === "per-team" ? "border-purple-400 bg-purple-500/20 text-purple-300" : "border-slate-800 text-slate-500 hover:border-slate-600"}`}>
                Per squadra
              </button>
            </div>

            {resetBudgetsMode === "default" && (
              <div className="flex items-center gap-3 p-3 rounded-2xl border border-amber-500/30 bg-amber-500/5">
                <span className="text-xs text-slate-400 font-bold uppercase">Budget uguale a</span>
                <input type="number" min="0" step="1" value={budgetDefault} onChange={e => setBudgetDefault(e.target.value)}
                  data-testid="reset-budget-default-input"
                  className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-sm font-mono w-32"/>
                <span className="text-xs text-slate-500">cr per ogni squadra</span>
              </div>
            )}

            {resetBudgetsMode === "per-team" && (
              <div className="p-3 rounded-2xl border border-purple-500/30 bg-purple-500/5 space-y-2 max-h-64 overflow-y-auto">
                <div className="text-[10px] text-slate-400">Inserisci i crediti rimanenti dopo l&apos;asta per ogni squadra:</div>
                {teams.map(t => (
                  <div key={t.team_id} className="flex items-center gap-2" data-testid={`reset-budget-team-${t.team_id}`}>
                    <div className="flex-1 font-bold text-sm">⚽ {t.name}</div>
                    <input type="number" min="0" step="1" value={perTeam[t.team_id] ?? 0}
                      onChange={e => setPerTeam(prev => ({ ...prev, [t.team_id]: e.target.value }))}
                      className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-sm font-mono w-28 text-right"/>
                    <span className="text-[10px] text-slate-500 w-6">cr</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={run} disabled={running} data-testid="reset-season-execute"
            className="w-full px-4 py-3 rounded-2xl bg-gradient-to-br from-rose-500 to-rose-700 hover:from-rose-400 hover:to-rose-600 text-white font-black uppercase text-sm shadow-lg shadow-rose-500/40 disabled:opacity-50">
            {running ? "In esecuzione…" : "🔄 Esegui reset stagione"}
          </button>

          <div className="text-[10px] text-slate-500 border-l-2 border-amber-500 pl-2">
            💡 Dopo il reset, usa <strong className="text-amber-300">Import</strong> per caricare le nuove rose finali via Excel/CSV.
          </div>
        </div>
      )}
    </div>
  );
}

function MiniInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-950/60 border border-slate-800 p-2 text-center">
      <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-base font-mono font-black">{value ?? "—"}</div>
    </div>
  );
}


/* ==================== TAB: FINANCE CONFIG ==================== */
function FinanceConfigTab({ league, reload }) {
  const [section, setSection] = useState("bands");
  return (
    <div className="space-y-4">
      <div className="border border-emerald-500/30 rounded-2xl bg-emerald-500/5 p-4 text-sm text-slate-300">
        <div className="flex items-start gap-2">
          <span className="text-2xl">💡</span>
          <div>
            <div className="font-black text-emerald-300 mb-1">Motore economico parametrico</div>
            Tutte le tabelle sotto vengono lette al volo dal calcolo Finanze. Nessun valore è hardcoded: puoi
            modellare qualsiasi regolamento senza toccare il codice.
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-800 overflow-x-auto">
        <FinTab active={section==="bands"} onClick={()=>setSection("bands")} label="🛍️ Fasce Merch"/>
        <FinTab active={section==="reval"} onClick={()=>setSection("reval")} label="✨ Rivalutazione"/>
        <FinTab active={section==="ranking"} onClick={()=>setSection("ranking")} label="⭐ Punti Ranking"/>
        <FinTab active={section==="stadium"} onClick={()=>setSection("stadium")} label="🏟️ Stadio"/>
        <FinTab active={section==="tv"} onClick={()=>setSection("tv")} label="📺 Diritti TV"/>
      </div>

      {section === "bands" && <MerchBandsEditor league={league} reload={reload}/>}
      {section === "reval" && <MerchRevaluationEditor league={league} reload={reload}/>}
      {section === "ranking" && <RankingPointsEditor league={league} reload={reload}/>}
      {section === "stadium" && <StadiumConfigEditor league={league} reload={reload}/>}
      {section === "tv" && <TvRightsEditor league={league} reload={reload}/>}
    </div>
  );
}

function FinTab({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-2 text-xs font-black uppercase tracking-wider border-b-2 -mb-px whitespace-nowrap ${
        active ? "border-emerald-400 text-emerald-300" : "border-transparent text-slate-400 hover:text-white"
      }`}>{label}</button>
  );
}

async function saveFinance(leagueId, payload, reload) {
  try {
    await api.put(`/leagues/${leagueId}/settings/finances`, payload);
    toast.success("✅ Configurazione salvata");
    await reload();
  } catch (e) { toast.error(formatApiError(e)); }
}

function MerchBandsEditor({ league, reload }) {
  const [bands, setBands] = useState(league.settings?.merchandising_bands || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setBands(league.settings?.merchandising_bands || []); }, [league.settings]);

  const upd = (i, k, v) => setBands(prev => prev.map((b, idx) => idx === i ? { ...b, [k]: v === "" ? null : v } : b));
  const add = () => setBands(prev => [...prev, { min_cost: 0, max_cost: 0, bonus: 0 }]);
  const del = (i) => setBands(prev => prev.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    const clean = bands.map(b => ({
      min_cost: Number(b.min_cost) || 0,
      max_cost: b.max_cost === null || b.max_cost === "" ? null : Number(b.max_cost),
      bonus: Number(b.bonus) || 0,
    }));
    await saveFinance(league.league_id, { merchandising_bands: clean }, reload);
    setSaving(false);
  };
  return (
    <div className="border border-slate-800 rounded-3xl bg-slate-900/50 p-5 shadow-xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-black uppercase text-sm tracking-wider">🛍️ Fasce Merchandising (per giocatore)</h3>
          <p className="text-slate-400 text-xs mt-0.5">Il bonus di ogni giocatore si somma nel merch base della squadra. Lascia <code>max</code> vuoto per &quot;senza limite&quot;.</p>
        </div>
        <button onClick={add} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-black" data-testid="add-band-btn">
          <Plus size={12}/> Aggiungi
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-2 py-2">Min acquisto</th><th className="text-left px-2 py-2">Max acquisto</th><th className="text-left px-2 py-2">Bonus merch</th><th></th></tr>
          </thead>
          <tbody>
            {bands.map((b, i) => (
              <tr key={i} className="border-t border-slate-800" data-testid={`band-row-${i}`}>
                <td className="px-2 py-2"><input type="number" value={b.min_cost ?? 0} onChange={(e)=>upd(i, "min_cost", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-24 font-mono"/></td>
                <td className="px-2 py-2"><input type="number" value={b.max_cost ?? ""} placeholder="∞ (vuoto)" onChange={(e)=>upd(i, "max_cost", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-24 font-mono"/></td>
                <td className="px-2 py-2"><input type="number" value={b.bonus ?? 0} onChange={(e)=>upd(i, "bonus", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-24 font-mono"/></td>
                <td className="px-2 py-2 text-right"><button onClick={()=>del(i)} className="text-rose-400 hover:text-rose-300"><Trash2 size={14}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={save} disabled={saving} data-testid="save-bands-btn"
        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase">
        {saving ? "Salvo…" : "💾 Salva fasce merch"}
      </button>
    </div>
  );
}

function MerchRevaluationEditor({ league, reload }) {
  const [cfg, setCfg] = useState(league.settings?.merch_revaluation || { enabled: true, rules: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCfg(league.settings?.merch_revaluation || { enabled: true, rules: [] }); }, [league.settings]);
  const rules = cfg.rules || [];
  const upd = (i, k, v) => setCfg(prev => ({ ...prev, rules: prev.rules.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const add = () => setCfg(prev => ({ ...prev, rules: [...(prev.rules||[]), { type: "roster_growth", min_pct: 20, multiplier: 0.1, label: "Nuova regola" }] }));
  const del = (i) => setCfg(prev => ({ ...prev, rules: prev.rules.filter((_, idx) => idx !== i) }));
  const save = async () => {
    setSaving(true);
    const clean = {
      enabled: !!cfg.enabled,
      rules: rules.map(r => ({
        type: r.type,
        label: r.label || "",
        multiplier: Number(r.multiplier) || 0,
        ...(r.type === "roster_growth" ? { min_pct: Number(r.min_pct) || 0 } : {}),
        ...(r.type === "position" ? { competition_id: r.competition_id || "scudetto", max_position: Number(r.max_position) || 1 } : {}),
        ...(r.type === "ranking" ? { min_ranking: Number(r.min_ranking) || 0 } : {}),
      })),
    };
    await saveFinance(league.league_id, { merch_revaluation: clean }, reload);
    setSaving(false);
  };
  const competitions = league.settings?.competitions || [];
  return (
    <div className="border border-slate-800 rounded-3xl bg-slate-900/50 p-5 shadow-xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-black uppercase text-sm tracking-wider">✨ Rivalutazione Merchandising</h3>
          <p className="text-slate-400 text-xs mt-0.5">Moltiplicatori applicati sul merch base a fine stagione.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs flex items-center gap-1">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e)=>setCfg(prev=>({...prev, enabled:e.target.checked}))}/>
            Abilita rivalutazione
          </label>
          <button onClick={add} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-black">
            <Plus size={12}/> Regola
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {rules.map((r, i) => (
          <div key={i} className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
            <div className="grid sm:grid-cols-6 gap-2 items-center">
              <input type="text" value={r.label || ""} onChange={(e)=>upd(i, "label", e.target.value)} placeholder="Etichetta"
                className="sm:col-span-2 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm"/>
              <select value={r.type} onChange={(e)=>upd(i, "type", e.target.value)}
                className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm">
                <option value="roster_growth">Crescita rosa</option>
                <option value="position">Piazzamento</option>
                <option value="ranking">Ranking storico</option>
              </select>
              {r.type === "roster_growth" && (
                <input type="number" value={r.min_pct ?? 0} onChange={(e)=>upd(i, "min_pct", e.target.value)}
                  placeholder="min %" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono"/>
              )}
              {r.type === "position" && (
                <>
                  <select value={r.competition_id || ""} onChange={(e)=>upd(i, "competition_id", e.target.value)}
                    className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm">
                    {competitions.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                  </select>
                  <input type="number" value={r.max_position ?? 1} onChange={(e)=>upd(i, "max_position", e.target.value)}
                    placeholder="pos ≤" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono"/>
                </>
              )}
              {r.type === "ranking" && (
                <input type="number" value={r.min_ranking ?? 0} onChange={(e)=>upd(i, "min_ranking", e.target.value)}
                  placeholder="min ranking" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono"/>
              )}
              <div className="flex items-center gap-1">
                <input type="number" step="0.01" value={r.multiplier ?? 0} onChange={(e)=>upd(i, "multiplier", e.target.value)}
                  placeholder="mult." className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono w-full"/>
                <button onClick={()=>del(i)} className="text-rose-400 hover:text-rose-300"><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving}
        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase">
        {saving ? "Salvo…" : "💾 Salva rivalutazione"}
      </button>
    </div>
  );
}

function RankingPointsEditor({ league, reload }) {
  const [rows, setRows] = useState(league.settings?.ranking_points || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRows(league.settings?.ranking_points || []); }, [league.settings]);
  const upd = (i, k, v) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const add = () => setRows(prev => [...prev, { competition_id: "scudetto", position: 1, points: 100 }]);
  const del = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    const clean = rows.map(r => ({
      competition_id: r.competition_id,
      position: Number(r.position) || 0,
      points: Number(r.points) || 0,
    }));
    await saveFinance(league.league_id, { ranking_points: clean }, reload);
    setSaving(false);
  };
  const competitions = league.settings?.competitions || [];
  return (
    <div className="border border-slate-800 rounded-3xl bg-slate-900/50 p-5 shadow-xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-black uppercase text-sm tracking-wider">⭐ Punti Ranking per posizione</h3>
          <p className="text-slate-400 text-xs mt-0.5">Il ranking stagionale = somma dei punti guadagnati. Il ranking storico si somma a fine stagione.</p>
        </div>
        <button onClick={add} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-black">
          <Plus size={12}/> Riga
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-2 py-2">Competizione</th><th className="text-left px-2 py-2">Posizione</th><th className="text-left px-2 py-2">Punti</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-800">
                <td className="px-2 py-2">
                  <select value={r.competition_id} onChange={(e)=>upd(i, "competition_id", e.target.value)}
                    className="bg-slate-950 border border-slate-700 rounded px-2 py-1">
                    {competitions.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                  </select>
                </td>
                <td className="px-2 py-2"><input type="number" value={r.position} onChange={(e)=>upd(i, "position", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-20 font-mono"/></td>
                <td className="px-2 py-2"><input type="number" value={r.points} onChange={(e)=>upd(i, "points", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-24 font-mono"/></td>
                <td className="px-2 py-2 text-right"><button onClick={()=>del(i)} className="text-rose-400"><Trash2 size={14}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={save} disabled={saving}
        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase">
        {saving ? "Salvo…" : "💾 Salva ranking"}
      </button>
    </div>
  );
}

function StadiumConfigEditor({ league, reload }) {
  const [levels, setLevels] = useState(league.settings?.stadium_levels || []);
  const [bonuses, setBonuses] = useState(league.settings?.stadium_bonuses || { position: [], ranking: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setLevels(league.settings?.stadium_levels || []);
    setBonuses(league.settings?.stadium_bonuses || { position: [], ranking: [] });
  }, [league.settings]);
  const competitions = league.settings?.competitions || [];

  const upLvl = (i, k, v) => setLevels(prev => prev.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLvl = () => setLevels(prev => [...prev, { level: prev.length + 1, cost: 0, income: 0, maintenance: 0 }]);
  const delLvl = (i) => setLevels(prev => prev.filter((_, idx) => idx !== i));

  const upBonus = (kind, i, k, v) => setBonuses(prev => ({ ...prev, [kind]: prev[kind].map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const addBonus = (kind) => setBonuses(prev => ({ ...prev, [kind]: [...(prev[kind]||[]),
    kind === "position" ? { competition_id: "scudetto", max_position: 1, multiplier: 0.1, label: "Nuovo bonus" }
                        : { min_ranking: 1000, multiplier: 0.1, label: "Nuovo bonus" }
  ]}));
  const delBonus = (kind, i) => setBonuses(prev => ({ ...prev, [kind]: prev[kind].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    const cleanLevels = levels.map((l, i) => ({
      level: Number(l.level) || i + 1,
      cost: Number(l.cost) || 0,
      income: Number(l.income) || 0,
      maintenance: Number(l.maintenance) || 0,
    }));
    const cleanBonuses = {
      position: (bonuses.position || []).map(b => ({
        competition_id: b.competition_id || "scudetto",
        max_position: Number(b.max_position) || 1,
        multiplier: Number(b.multiplier) || 0,
        label: b.label || "",
      })),
      ranking: (bonuses.ranking || []).map(b => ({
        min_ranking: Number(b.min_ranking) || 0,
        multiplier: Number(b.multiplier) || 0,
        label: b.label || "",
      })),
    };
    await saveFinance(league.league_id, { stadium_levels: cleanLevels, stadium_bonuses: cleanBonuses }, reload);
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="border border-slate-800 rounded-3xl bg-slate-900/50 p-5 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black uppercase text-sm tracking-wider">🏟️ Livelli stadio</h3>
          <button onClick={addLvl} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-black">
            <Plus size={12}/> Livello
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500">
              <tr><th className="text-left px-2 py-2">Livello</th><th className="text-left px-2 py-2">Costo</th><th className="text-left px-2 py-2">Ricavo base</th><th className="text-left px-2 py-2">Manutenzione</th><th></th></tr>
            </thead>
            <tbody>
              {levels.map((l, i) => (
                <tr key={i} className="border-t border-slate-800">
                  <td className="px-2 py-2"><input type="number" value={l.level} onChange={(e)=>upLvl(i, "level", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-16 font-mono"/></td>
                  <td className="px-2 py-2"><input type="number" value={l.cost} onChange={(e)=>upLvl(i, "cost", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-24 font-mono"/></td>
                  <td className="px-2 py-2"><input type="number" value={l.income} onChange={(e)=>upLvl(i, "income", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-24 font-mono"/></td>
                  <td className="px-2 py-2"><input type="number" step="0.1" value={l.maintenance ?? 0} onChange={(e)=>upLvl(i, "maintenance", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-24 font-mono"/></td>
                  <td className="px-2 py-2 text-right"><button onClick={()=>delLvl(i)} className="text-rose-400"><Trash2 size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-slate-800 rounded-3xl bg-slate-900/50 p-5 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black uppercase text-sm tracking-wider">✨ Bonus stadio da piazzamento</h3>
          <button onClick={()=>addBonus("position")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-black">
            <Plus size={12}/> Bonus
          </button>
        </div>
        {(bonuses.position || []).map((b, i) => (
          <div key={i} className="grid sm:grid-cols-5 gap-2 items-center py-2 border-t border-slate-800">
            <input type="text" value={b.label || ""} onChange={(e)=>upBonus("position", i, "label", e.target.value)} placeholder="Etichetta" className="sm:col-span-2 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm"/>
            <select value={b.competition_id} onChange={(e)=>upBonus("position", i, "competition_id", e.target.value)} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm">
              {competitions.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
            </select>
            <input type="number" value={b.max_position ?? 1} onChange={(e)=>upBonus("position", i, "max_position", e.target.value)} placeholder="pos ≤" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono"/>
            <div className="flex items-center gap-1">
              <input type="number" step="0.01" value={b.multiplier ?? 0} onChange={(e)=>upBonus("position", i, "multiplier", e.target.value)} placeholder="+%" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono w-full"/>
              <button onClick={()=>delBonus("position", i)} className="text-rose-400"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-slate-800 rounded-3xl bg-slate-900/50 p-5 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black uppercase text-sm tracking-wider">🏛️ Bonus stadio da ranking storico</h3>
          <button onClick={()=>addBonus("ranking")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-black">
            <Plus size={12}/> Bonus
          </button>
        </div>
        {(bonuses.ranking || []).map((b, i) => (
          <div key={i} className="grid sm:grid-cols-4 gap-2 items-center py-2 border-t border-slate-800">
            <input type="text" value={b.label || ""} onChange={(e)=>upBonus("ranking", i, "label", e.target.value)} placeholder="Etichetta" className="sm:col-span-2 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm"/>
            <input type="number" value={b.min_ranking ?? 0} onChange={(e)=>upBonus("ranking", i, "min_ranking", e.target.value)} placeholder="min ranking" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono"/>
            <div className="flex items-center gap-1">
              <input type="number" step="0.01" value={b.multiplier ?? 0} onChange={(e)=>upBonus("ranking", i, "multiplier", e.target.value)} placeholder="+%" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono w-full"/>
              <button onClick={()=>delBonus("ranking", i)} className="text-rose-400"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase">
        {saving ? "Salvo…" : "💾 Salva configurazione stadio"}
      </button>
    </div>
  );
}

function TvRightsEditor({ league, reload }) {
  const [cfg, setCfg] = useState(league.settings?.tv_rights_config || { base: 0, position_bonus: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCfg(league.settings?.tv_rights_config || { base: 0, position_bonus: [] }); }, [league.settings]);
  const upd = (i, k, v) => setCfg(prev => ({ ...prev, position_bonus: prev.position_bonus.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const add = () => setCfg(prev => ({ ...prev, position_bonus: [...(prev.position_bonus||[]), { max_position: 1, amount: 10 }] }));
  const del = (i) => setCfg(prev => ({ ...prev, position_bonus: prev.position_bonus.filter((_, idx) => idx !== i) }));
  const save = async () => {
    setSaving(true);
    const clean = {
      base: Number(cfg.base) || 0,
      position_bonus: (cfg.position_bonus || []).map(b => ({ max_position: Number(b.max_position) || 1, amount: Number(b.amount) || 0 })),
    };
    await saveFinance(league.league_id, { tv_rights_config: clean }, reload);
    setSaving(false);
  };
  return (
    <div className="border border-slate-800 rounded-3xl bg-slate-900/50 p-5 shadow-xl">
      <h3 className="font-black uppercase text-sm tracking-wider mb-3">📺 Diritti TV</h3>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm">Base per tutte le squadre:</span>
        <input type="number" value={cfg.base ?? 0} onChange={(e)=>setCfg(prev=>({...prev, base:e.target.value}))} className="bg-slate-950 border border-slate-700 rounded px-2 py-1 w-24 font-mono"/>
        <span className="text-xs text-slate-500">cr</span>
      </div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Bonus TV per posizione Scudetto</div>
        <button onClick={add} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-black">
          <Plus size={12}/> Bonus
        </button>
      </div>
      {(cfg.position_bonus || []).map((b, i) => (
        <div key={i} className="grid sm:grid-cols-3 gap-2 items-center py-2 border-t border-slate-800">
          <input type="number" value={b.max_position ?? 1} onChange={(e)=>upd(i, "max_position", e.target.value)} placeholder="pos ≤" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono"/>
          <input type="number" value={b.amount ?? 0} onChange={(e)=>upd(i, "amount", e.target.value)} placeholder="importo" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm font-mono"/>
          <button onClick={()=>del(i)} className="text-rose-400 justify-self-start"><Trash2 size={14}/></button>
        </div>
      ))}
      <button onClick={save} disabled={saving}
        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase">
        {saving ? "Salvo…" : "💾 Salva diritti TV"}
      </button>
    </div>
  );
}


const OP_META = {
  acquisto:         { emoji: "🛒", label: "Acquisto",         color: "amber"  },
  cessione:         { emoji: "🔄", label: "Cessione",         color: "sky"    },
  cessione_esterno: { emoji: "🚪", label: "Cessione esterno", color: "rose"   },
  scambio:          { emoji: "🤝", label: "Scambio",          color: "purple" },
  prestito:         { emoji: "🕰️", label: "Prestito",          color: "cyan"   },
  riscatto:         { emoji: "💫", label: "Riscatto",         color: "indigo" },
  svincolo:         { emoji: "🏳️", label: "Svincolo",          color: "orange" },
  rata:             { emoji: "📅", label: "Rata",             color: "slate"  },
  bonus:            { emoji: "🎁", label: "Bonus",            color: "emerald"},
};

function UndoLogTab({ league }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/leagues/${league.league_id}/operations/reverted?limit=200`);
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); setItems([]); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [league.league_id]);

  const filtered = (items || []).filter(o => filter === "all" || o.op_type === filter);
  const uniqueTypes = [...new Set((items || []).map(o => o.op_type))];

  return (
    <div className="space-y-4">
      <div className="border border-rose-500/25 rounded-3xl bg-gradient-to-br from-rose-500/5 to-slate-950 p-6 shadow-xl shadow-black/40">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.3em] text-rose-400 uppercase font-black">⏪ Undo Log</div>
            <h3 className="text-xl font-black text-white mt-1">Storico annullamenti</h3>
            <div className="text-xs text-slate-400 mt-1 max-w-xl">
              Ogni operazione annullata resta tracciata qui: chi l&apos;ha creata, chi l&apos;ha annullata, quando. Nessuna azione richiesta — solo lettura.
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-slate-500">Totale annullate:</span>
            <span className="font-black text-rose-300 text-lg" data-testid="undo-log-count">{items?.length ?? "—"}</span>
          </div>
        </div>

        {/* Filter chips */}
        {uniqueTypes.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <FilterChip active={filter==="all"} onClick={()=>setFilter("all")} label={`Tutte (${items?.length ?? 0})`} tid="filter-all"/>
            {uniqueTypes.map(t => {
              const meta = OP_META[t] || { emoji: "•", label: t };
              const count = (items || []).filter(o => o.op_type === t).length;
              return <FilterChip key={t} active={filter===t} onClick={()=>setFilter(t)} label={`${meta.emoji} ${meta.label} (${count})`} tid={`filter-${t}`}/>;
            })}
          </div>
        )}
      </div>

      {busy && !items && <div className="text-sm text-slate-500 py-6 text-center">Caricamento…</div>}
      {items && filtered.length === 0 && (
        <div className="rounded-3xl border border-slate-800 bg-slate-950/60 p-10 text-center" data-testid="undo-log-empty">
          <div className="text-4xl mb-2">✨</div>
          <div className="text-sm text-slate-400">Nessuna operazione annullata{filter !== "all" ? " di questo tipo" : ""}.</div>
          <div className="text-[10px] text-slate-600 mt-1">Le operazioni annullate compaiono qui in tempo reale.</div>
        </div>
      )}

      {items && filtered.length > 0 && (
        <div className="rounded-3xl border border-slate-800 bg-slate-950/40 overflow-hidden" data-testid="undo-log-list">
          <ul className="divide-y divide-slate-800/60">
            {filtered.map(o => {
              const meta = OP_META[o.op_type] || { emoji: "•", label: o.op_type, color: "slate" };
              const createdAt = o.created_at ? new Date(o.created_at) : null;
              const reversedAt = o.reversed_at ? new Date(o.reversed_at) : null;
              return (
                <li key={o.op_id} className="p-4 flex gap-4 items-start hover:bg-slate-900/30" data-testid={`undo-log-item-${o.op_id}`}>
                  <div className="text-2xl shrink-0 mt-1">{meta.emoji}</div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-${meta.color}-500/15 text-${meta.color}-300 border border-${meta.color}-500/30`}>{meta.label}</span>
                      <span className="font-bold text-white">{o.player_name || "—"}</span>
                      {o.from_team_name && <span className="text-xs text-slate-500">da <b className="text-slate-300">{o.from_team_name}</b></span>}
                      {o.to_team_name && <span className="text-xs text-slate-500">a <b className="text-slate-300">{o.to_team_name}</b></span>}
                      {o.amount > 0 && <span className="text-xs font-mono text-amber-400">€{o.amount}</span>}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-[11px]">
                      <span className="text-slate-500">Creata da <b className="text-slate-300">{o.created_by_name || "?"}</b></span>
                      {createdAt && <span className="text-slate-600 font-mono">{createdAt.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-[11px]">
                      <span className="inline-flex items-center gap-1 text-rose-400"><Undo2 size={10}/> Annullata da <b className="text-rose-300">{o.reversed_by_name || "?"}</b></span>
                      {reversedAt && <span className="text-slate-600 font-mono">{reversedAt.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                    </div>
                    {o.note && <div className="text-[10px] text-slate-600 italic mt-1 truncate">📝 {o.note}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, tid }) {
  return (
    <button onClick={onClick} data-testid={tid}
      className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all border ${
        active
          ? "bg-rose-500/20 border-rose-500/60 text-rose-200 shadow-lg shadow-rose-500/10"
          : "bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200"
      }`}>
      {label}
    </button>
  );
}

